import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Clock, AlertCircle } from "lucide-react";

export default function SubmissionConfirmation({ port, tripData, entryPackData, onComplete, onBack }) {
  const [status, setStatus] = useState("submitting");

  useEffect(() => {
    const timer = setTimeout(() => {
      setStatus("confirmed");
    }, 2500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="space-y-6">
      {/* Success/Confirmation */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 border-green-200 dark:border-green-800">
          <CardContent className="pt-8 pb-8 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
              className="mb-4"
            >
              <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto" />
            </motion.div>
            <h2 className="text-2xl font-bold text-green-900 dark:text-green-100 mb-2">
              {status === "confirmed" ? "✓ Entry Pack Sent!" : "Submitting..."}
            </h2>
            <p className="text-green-800 dark:text-green-200 mb-4">
              {status === "confirmed"
                ? `Your entry pack for ${port.name} has been submitted successfully.`
                : "Your entry pack is being transmitted to port authorities..."}
            </p>
            {status === "confirmed" && (
              <p className="text-xs text-green-700 dark:text-green-300">
                Submitted on {new Date().toLocaleString()}
              </p>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Tracking */}
      {status === "confirmed" && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="text-base">📍 Submission Tracking</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { status: "completed", label: "Submitted", time: "Just now" },
                { status: "pending", label: "Awaiting Confirmation", time: "2-4 hours" },
                { status: "pending", label: "Approved / Issue Notification", time: "24 hours" }
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    item.status === "completed"
                      ? "bg-green-100 text-green-800"
                      : "bg-muted text-muted-foreground"
                  }`}>
                    {item.status === "completed" ? "✓" : idx + 1}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold">{item.label}</p>
                    <p className="text-xs text-muted-foreground">{item.time}</p>
                  </div>
                  {item.status === "completed" && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                  {item.status === "pending" && <Clock className="w-4 h-4 text-yellow-500" />}
                </div>
              ))}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Port Details */}
      {status === "confirmed" && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="text-base">📋 Entry Pack Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Port</p>
                  <p className="font-semibold">{port.name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Vessel</p>
                  <p className="font-semibold">{tripData.boat.name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">ETA</p>
                  <p className="font-semibold">{tripData.eta}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Documents</p>
                  <p className="font-semibold">{Object.values(entryPackData.documents).filter(Boolean).length} submitted</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Next Steps */}
      {status === "confirmed" && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
        >
          <Card className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
            <CardContent className="pt-6">
              <p className="text-sm font-semibold mb-2">What's next?</p>
              <ul className="space-y-1 text-xs text-muted-foreground list-disc list-inside">
                <li>Keep your entry pack saved for reference</li>
                <li>Check back for confirmation status (usually 2-4 hours)</li>
                <li>Port will contact you if additional documents are needed</li>
              </ul>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Actions */}
      <div className="flex gap-3 justify-end sticky bottom-0 bg-background pt-4 border-t border-border">
        {status === "submitting" && (
          <div className="text-sm text-muted-foreground">Processing...</div>
        )}
        {status === "confirmed" && (
          <>
            <Button variant="outline" onClick={onBack}>Back</Button>
            <Button onClick={onComplete} className="gap-2">
              View Entry History →
            </Button>
          </>
        )}
      </div>
    </div>
  );
}